import { useEffect, useId, useState } from "react";
import { createPortal } from "react-dom";
import { Icon } from "@/components/icons";
import { usePopoverPosition } from "@/components/use-popover-position";

export type SelectOption = { value: string; label: string };

type Props = {
  label: string;
  value: string | null;
  options: SelectOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  id?: string;
};

/**
 * Brand dropdown select — label + button + listbox. The listbox is PORTALED to
 * document.body and positioned (z-[1400], above the MUI Dialog at 1300) so that
 * opening it inside a modal never grows the modal content or triggers an ugly
 * nested scrollbar. Same approach as filter-cards.tsx EntityTypeSelect (proven
 * inside the monitoring add-name Dialog). Used by the Invite/Edit member modals.
 */
export function SelectField({
  label,
  value,
  options,
  onChange,
  placeholder = "Select",
  id,
}: Props) {
  const [open, setOpen] = useState(false);
  const [trigger, setTrigger] = useState<HTMLButtonElement | null>(null);
  const [popover, setPopover] = useState<HTMLDivElement | null>(null);
  const reactId = useId();
  const fieldId = id ?? reactId;
  const selected = options.find((o) => o.value === value);
  const pos = usePopoverPosition({
    anchorEl: open ? trigger : null,
    popoverEl: popover,
    align: "stretch",
  });

  useEffect(() => {
    if (!open) return;
    function onPointer(e: MouseEvent) {
      const inTrigger = trigger?.contains(e.target as Node);
      const inPopover = popover?.contains(e.target as Node);
      if (!inTrigger && !inPopover) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, trigger, popover]);

  return (
    <div className="flex w-full flex-col gap-1">
      <label htmlFor={fieldId} className="text-small font-medium text-grey-900">
        {label}
      </label>
      <button
        ref={setTrigger}
        id={fieldId}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="flex w-full items-center gap-2 rounded-[4px] border border-grey-200 bg-white px-3 py-2 text-left shadow-flat transition-colors hover:border-grey-300"
      >
        <span
          className={[
            "min-w-0 flex-1 truncate text-body",
            selected ? "text-grey-900" : "text-grey-500",
          ].join(" ")}
        >
          {selected ? selected.label : placeholder}
        </span>
        <Icon
          name={open ? "keyboard_arrow_up" : "keyboard_arrow_down"}
          size={20}
          className="shrink-0 text-grey-600"
        />
      </button>
      {open &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            ref={setPopover}
            role="listbox"
            aria-label={label}
            style={{
              top: pos?.top ?? 0,
              left: pos?.left ?? 0,
              width: pos?.width,
              visibility: pos ? "visible" : "hidden",
            }}
            className="fixed z-[1400] flex max-h-[260px] flex-col overflow-y-auto rounded-[4px] border border-grey-200 bg-white p-1 shadow-soft"
          >
            {options.map((o) => {
              const isSelected = o.value === value;
              return (
                <button
                  key={o.value}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => {
                    onChange(o.value);
                    setOpen(false);
                  }}
                  className={[
                    "flex items-center justify-between gap-2 rounded-[4px] px-3 py-2 text-left text-body font-medium transition-colors",
                    isSelected ? "bg-grey-100 text-black" : "text-black hover:bg-grey-50",
                  ].join(" ")}
                >
                  <span>{o.label}</span>
                  {isSelected && <Icon name="check" size={20} className="text-green-500" />}
                </button>
              );
            })}
          </div>,
          document.body,
        )}
    </div>
  );
}
